import { useEffect, useRef } from "react"
import { batch } from "@preact/signals-react"

import { BACKEND_APIS } from "../@constants"
import {
	aiInsightsData, aiInsightsText, aiInsightsError,
	isAnalyzingAi, isOpenAiInsights, closeAiInsights
} from "../@signals"


async function analyzeRequest(data, abortSignal) {
	const response = await fetch(`${BACKEND_APIS.AI_INSIGHTS}/analyze`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(data),
		signal: abortSignal,
	})

	if (!response.ok)
		throw new Error(`Server error: ${response.status}`)

	const contentType = response.headers.get('content-type') || ''

	// Streaming response (chunked JSON lines)
	if (contentType.includes('text/event-stream') || contentType.includes('ndjson')) {
		return readStream(response)
	}

	// Standard JSON response — try to detect format
	const text = await response.text()
	try {
		const json = JSON.parse(text)

		// Streaming format: { type: "final", data: { response } }
		if (json.type === 'final') {
			aiInsightsText.value = json.data.response
			return
		}
		if (json.type === 'error') {
			aiInsightsError.value = json.message
			return
		}

		// Direct format: { response: "...", insights: "..." }
		if (json.response) {
			aiInsightsText.value = json.response
			return
		}
		if (json.insights) {
			aiInsightsText.value = json.insights
			return
		}

		// Unknown format — show raw
		aiInsightsText.value = text
	} catch {
		// Multi-line JSON (streaming as plain text)
		return parseStreamLines(text)
	}
}


function parseStreamLines(text) {
	const lines = text.split('\n')
	for (const line of lines) {
		if (!line.trim()) continue
		try {
			const msg = JSON.parse(line)
			if (msg.type === 'chunk')
				aiInsightsText.value += msg.text
			else if (msg.type === 'final')
				aiInsightsText.value = msg.data.response
			else if (msg.type === 'error')
				aiInsightsError.value = msg.message
		} catch { /* skip malformed lines */ }
	}
}


async function readStream(response) {
	const reader = response.body.getReader()
	const decoder = new TextDecoder()
	let buffer = ''

	while (true) {
		const { done, value } = await reader.read()
		if (done) break

		buffer += decoder.decode(value, { stream: true })
		const lines = buffer.split('\n')
		buffer = lines.pop()

		for (const line of lines) {
			if (!line.trim()) continue
			try {
				const msg = JSON.parse(line)
				if (msg.type === 'chunk')
					aiInsightsText.value += msg.text
				else if (msg.type === 'final')
					aiInsightsText.value = msg.data.response
				else if (msg.type === 'error')
					aiInsightsError.value = msg.message
			} catch { /* skip malformed lines */ }
		}
	}
}


export default function useAiInsights() {
	const abortRef = useRef(null)

	const prevDataRef = useRef(null)

	useEffect(() => {
		const data = aiInsightsData.value
		if (!data || data === prevDataRef.current) return
		prevDataRef.current = data

		// Abort any previous request
		abortRef.current?.abort()
		const controller = new AbortController()
		abortRef.current = controller

		batch(() => {
			aiInsightsText.value = ''
			aiInsightsError.value = null
			isAnalyzingAi.value = true
		})

		analyzeRequest(data, controller.signal)
			.catch(err => {
				if (err.name !== 'AbortError')
					aiInsightsError.value = err.message || 'Analysis failed'
			})
			.finally(() => {
				isAnalyzingAi.value = false
			})

		return () => controller.abort()
	}, [aiInsightsData.value])

	return {
		isOpen: isOpenAiInsights.value,
		isAnalyzing: isAnalyzingAi.value,
		text: aiInsightsText.value,
		error: aiInsightsError.value,
		close: closeAiInsights,
	}
}
